import React, { useState } from "react";
import { MapPin, Phone, Clock, Send, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { PageHero, Eyebrow, SectionTitle, GoldButton, MapsButton } from "@/components/shared";
import { Ornament } from "@/components/Ornament";
import { ReviewButtons } from "@/components/ReviewButtons";
import { IMAGES } from "@/data/treasures";
import client, { formatApiError } from "@/lib/api";

const KontaktPage = () => {
  const { t, lang, contact } = useLang();
  const ref = useReveal([lang]);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/contact", { ...form, lang });
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t.contact.form.error);
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full bg-cream border border-border rounded-xl px-5 py-3.5 font-sans text-espresso placeholder:text-espresso/40 focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-all";

  return (
    <div ref={ref}>
      <PageHero image={IMAGES.cafeWarm} eyebrow={t.contact.eyebrow} title={t.contact.title} lead={t.contact.lead} />

      <section className="py-24 md:py-32 bg-cream grain-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16">
          {/* Info + form */}
          <div data-reveal>
            <Eyebrow>{t.contact.eyebrow}</Eyebrow>
            <SectionTitle>{t.contact.title}</SectionTitle>

            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-espresso flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-gold" /></div>
                <div><span className="block text-xs tracking-widest uppercase text-espresso/50">{t.common.address}</span><span className="font-sans text-espresso">{contact.address}</span></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-espresso flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-gold" /></div>
                <div><span className="block text-xs tracking-widest uppercase text-espresso/50">{t.common.phone}</span><a href={`tel:${contact.phoneHref}`} className="font-sans text-espresso hover:text-gold-deep transition-colors" data-testid="contact-phone">{contact.phone}</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-espresso flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-gold" /></div>
                <div><span className="block text-xs tracking-widest uppercase text-espresso/50">E-Mail</span><a href={contact.emailHref} className="font-sans text-espresso hover:text-gold-deep transition-colors" data-testid="contact-email">{contact.email}</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-espresso flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-gold" /></div>
                <div><span className="block text-xs tracking-widest uppercase text-espresso/50">{t.common.openingHours}</span><span className="font-sans text-espresso">{t.common.monSat}, {t.common.hours}</span><span className="block text-espresso/50 text-sm">{t.common.sunday}</span></div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <MapsButton label={t.cta.route} query={contact.mapsQuery} testid="contact-maps-button" />
              <a href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent("Grüezi Mara-Team, ich habe eine Frage.")}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-button"
                className="inline-flex items-center gap-2.5 rounded-full bg-sage/90 px-6 py-3 text-cream font-sans text-sm tracking-wide transition-all hover:bg-sage active:scale-95">
                <MessageCircle className="w-4 h-4" /> {t.cta.whatsapp}
              </a>
            </div>

            <Ornament className="my-9 justify-start opacity-60" width={200} />

            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.contact.form.name} className={field} data-testid="contact-name-input" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t.contact.form.email} className={field} data-testid="contact-email-input" />
              <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t.contact.form.message} className={`${field} resize-none`} data-testid="contact-message-input" />
              <GoldButton as="button" type="submit" disabled={busy} data-testid="contact-submit-button">{t.cta.send} <Send className="w-4 h-4" /></GoldButton>
            </form>
          </div>

          {/* Map */}
          <div data-reveal data-reveal-delay="0.15" className="relative">
            <div className="sticky top-28 rounded-2xl overflow-hidden border border-border shadow-lg h-[560px]">
              <iframe
                title="Mara Wunderkammer Basel"
                data-testid="google-map"
                src={`https://maps.google.com/maps?q=${contact.mapsQuery}&z=16&output=embed`}
                className="w-full h-full grayscale-[0.2]"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Reviews & marketplaces */}
      <section className="py-20 md:py-28 bg-sand grain-overlay" data-testid="reviews-section">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <ReviewButtons />
        </div>
      </section>
    </div>
  );
};

export default KontaktPage;
